// =============================================
// NoteEntryEditor — the YKV entry editor (rich body + knowledge metadata)
// =============================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowLeft, FiStar, FiTrash2, FiPlus, FiSave, FiCheck, FiX, FiZap, FiBell } from 'react-icons/fi';
import { LuPin } from 'react-icons/lu';
import SearchableDropdown, { DropdownOption } from '../../components/ui/SearchableDropdown';
import { setFormDirty, registerSaveHandler } from '../../lib/unsavedChanges';
import NoteEditor from './NoteEditor';
import {
  NoteRow, CategoryRow, LinkRow, NotesBundle,
  updateNote, setNoteCategories, createCategory, parseContent,
  buildCategoryTree, flattenTree, parseList, joinList,
} from './notesLib';
import { extractKeywordsLocal, extractTagsLocal } from './kvKeywords';
import { aiSuggest } from './kvAi';

const AUTOSAVE_MS = 800;
const REPEAT_OPTS = [
  { label: 'One-off', days: 0 },
  { label: 'Every day', days: 1 },
  { label: 'Every week', days: 7 },
  { label: 'Every 2 weeks', days: 14 },
  { label: 'Every month', days: 30 },
  { label: 'Every 3 months', days: 90 },
];

function toDateInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

// ---- chip input for tags / keywords ----
const ChipInput: React.FC<{ values: string[]; onChange: (v: string[]) => void; placeholder: string }> = ({ values, onChange, placeholder }) => {
  const [draft, setDraft] = useState('');
  const add = (raw: string) => {
    const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const lower = new Set(values.map(v => v.toLowerCase()));
    const next = [...values];
    parts.forEach(p => { if (!lower.has(p.toLowerCase())) { lower.add(p.toLowerCase()); next.push(p); } });
    onChange(next);
    setDraft('');
  };
  return (
    <div className="kv-chips">
      {values.map(v => (
        <span key={v} className="kv-chip">{v}<button onClick={() => onChange(values.filter(x => x !== v))} aria-label={`Remove ${v}`}><FiX size={11} /></button></span>
      ))}
      <input
        className="kv-chip-input"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(draft); } else if (e.key === 'Backspace' && !draft && values.length) { onChange(values.slice(0, -1)); } }}
        onBlur={() => add(draft)}
        placeholder={values.length ? '' : placeholder}
      />
    </div>
  );
};

interface Props {
  note: NoteRow;
  categories: CategoryRow[];
  links: LinkRow[];
  aiEnabled: boolean;
  onBack: () => void;
  onDelete: () => void;
  onReload: () => Promise<NotesBundle>;
}

const NoteEntryEditor: React.FC<Props> = ({ note, categories, links, aiEnabled, onBack, onDelete, onReload }) => {
  const [title, setTitle] = useState(note.title || '');
  const [pinned, setPinned] = useState(note.is_pinned == 1); // eslint-disable-line eqeqeq
  const [important, setImportant] = useState(note.is_important == 1); // eslint-disable-line eqeqeq
  const [cats, setCats] = useState<CategoryRow[]>(categories);
  const [tags, setTags] = useState<string[]>(parseList(note.tags));
  const [keywords, setKeywords] = useState<string[]>(parseList(note.keywords));
  const [takeaway, setTakeaway] = useState(note.key_takeaway || '');
  const [reviewAt, setReviewAt] = useState(toDateInput(note.review_at));
  const [reviewEvery, setReviewEvery] = useState(Number(note.review_every_days) || 0);
  const [savedAt, setSavedAt] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [newCat, setNewCat] = useState('');
  const [aiMsg, setAiMsg] = useState<string | null>(null);
  const [aiBusy, setAiBusy] = useState(false);

  const initialCatIds = useMemo(
    () => links.filter(l => l.note_id === note.note_id && l.is_deleted != 1).map(l => String(l.category_id)), // eslint-disable-line eqeqeq
    [links, note.note_id]
  );
  const [selectedCatIds, setSelectedCatIds] = useState<string[]>(initialCatIds);

  const draft = useRef<{ json: any; text: string }>({ json: parseContent(note), text: note.content_text || '' });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const linksRef = useRef<LinkRow[]>(links); linksRef.current = links;
  // Latest field values for the flush closure.
  const fields = useRef<any>({});
  fields.current = { title, pinned, important, tags, keywords, takeaway, reviewAt, reviewEvery, selectedCatIds };

  const flush = useCallback(async (): Promise<boolean> => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    setSavedAt('saving');
    const f = fields.current;
    try {
      await updateNote(note.note_id, {
        title: f.title,
        content_json: JSON.stringify(draft.current.json),
        content_text: draft.current.text,
        is_pinned: f.pinned ? 1 : 0,
        is_important: f.important ? 1 : 0,
        tags: joinList(f.tags),
        keywords: joinList(f.keywords),
        key_takeaway: f.takeaway || null,
        review_at: f.reviewAt ? new Date(f.reviewAt + 'T09:00:00').toISOString() : null,
        review_every_days: f.reviewEvery || 0,
      });
      await setNoteCategories(note.note_id, f.selectedCatIds.map(Number), linksRef.current);
      setFormDirty(false);
      setSavedAt('saved');
      return true;
    } catch { setSavedAt('idle'); return false; }
  }, [note.note_id]);

  useEffect(() => { registerSaveHandler(() => flush()); return () => registerSaveHandler(null); }, [flush]);

  const markDirty = useCallback(() => {
    setFormDirty(true); setSavedAt('idle');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void flush(); }, AUTOSAVE_MS);
  }, [flush]);

  useEffect(() => () => { if (timer.current) { clearTimeout(timer.current); void flush(); } }, [flush]);

  const onEditorChange = useCallback((json: any, text: string) => { draft.current = { json, text }; markDirty(); }, [markDirty]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const markReviewed = useCallback(() => {
    if (reviewEvery > 0) setReviewAt(new Date(Date.now() + reviewEvery * 86400000).toISOString().slice(0, 10));
    else setReviewAt('');
    markDirty();
  }, [reviewEvery, markDirty]);

  const catOptions: DropdownOption[] = useMemo(() => {
    const tree = buildCategoryTree(cats);
    return flattenTree(tree).map(({ cat, depth }) => ({ value: String(cat.category_id), label: cat.category_name, depth }));
  }, [cats]);

  const addCategory = useCallback(async () => {
    const name = newCat.trim(); if (!name) return;
    const created = await createCategory(name, null);
    setNewCat('');
    const b = await onReload(); setCats(b.categories);
    setSelectedCatIds(prev => [...prev, String(created.category_id)]); markDirty();
  }, [newCat, onReload, markDirty]);

  const toggleCat = useCallback((value: string) => {
    setSelectedCatIds(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]); markDirty();
  }, [markDirty]);

  // ---- suggestions ----
  const suggestLocal = useCallback(() => {
    const text = `${title}\n${draft.current.text}`;
    setKeywords(prev => joinList([...prev, ...extractKeywordsLocal(text, 10)]).split(', ').filter(Boolean));
    setTags(prev => joinList([...prev, ...extractTagsLocal(text, 5)]).split(', ').filter(Boolean));
    setAiMsg(null); markDirty();
  }, [title, markDirty]);

  const suggestAi = useCallback(async () => {
    if (!aiEnabled) { setAiMsg('AI tagging is a paid feature — upgrade your plan to enable it. (Free suggestions still work.)'); return; }
    setAiBusy(true); setAiMsg(null);
    try {
      const r = await aiSuggest(title, draft.current.text);
      setTags(prev => joinList([...prev, ...(r.tags || [])]).split(', ').filter(Boolean));
      setKeywords(prev => joinList([...prev, ...(r.keywords || [])]).split(', ').filter(Boolean));
      if (r.mock) setAiMsg('(dev mock — no AI key configured)');
      markDirty();
    } catch (e: any) {
      setAiMsg(e?.code === 403 ? 'AI tagging is a paid feature — upgrade your plan to enable it.' : (e?.message || 'AI suggest failed'));
    } finally { setAiBusy(false); }
  }, [aiEnabled, title, markDirty]);

  return (
    <div className="note-edit-page">
      <div className="note-edit-bar">
        <button className="note-back" onClick={onBack}><FiArrowLeft size={16} /> Entries</button>
        <span className={`note-save-state ${savedAt}`}>
          {savedAt === 'saving' && <><FiSave size={13} /> Saving…</>}
          {savedAt === 'saved' && <><FiCheck size={13} /> Saved</>}
        </span>
        <div className="note-edit-actions">
          <button className={`note-flag ${pinned ? 'on' : ''}`} title="Pin" onClick={() => { setPinned(p => !p); markDirty(); }}><LuPin size={15} /></button>
          <button className={`note-flag ${important ? 'on' : ''}`} title="Important" onClick={() => { setImportant(p => !p); markDirty(); }}><FiStar size={15} /></button>
          <button className="note-flag danger" title="Delete" onClick={onDelete}><FiTrash2 size={15} /></button>
        </div>
      </div>

      <input className="note-title-input" value={title} onChange={e => { setTitle(e.target.value); markDirty(); }} placeholder="Title (optional)" maxLength={255} />

      <input
        className="kv-takeaway"
        value={takeaway}
        onChange={e => { setTakeaway(e.target.value); markDirty(); }}
        placeholder="💡 The one thing to remember…"
        maxLength={500}
      />

      <div className="kv-meta">
        <div className="kv-meta-row">
          <label>Categories</label>
          <div className="kv-meta-field">
            <SearchableDropdown multiSelect options={catOptions} selectedValues={selectedCatIds} onToggleValue={toggleCat} placeholder="Add to categories…" />
            <div className="note-cat-new">
              <input value={newCat} onChange={e => setNewCat(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addCategory(); } }} placeholder="New category" maxLength={255} />
              <button onClick={() => void addCategory()} title="Create category"><FiPlus size={14} /></button>
            </div>
          </div>
        </div>

        <div className="kv-meta-row">
          <label>Tags</label>
          <div className="kv-meta-field"><ChipInput values={tags} onChange={v => { setTags(v); markDirty(); }} placeholder="add a tag, press Enter…" /></div>
        </div>

        <div className="kv-meta-row">
          <label>Keywords</label>
          <div className="kv-meta-field">
            <ChipInput values={keywords} onChange={v => { setKeywords(v); markDirty(); }} placeholder="add keywords for better search…" />
            <div className="kv-suggest">
              <button className="kv-suggest-btn" onClick={suggestLocal} title="Extract keywords from this entry (free)">Suggest (free)</button>
              <button className={`kv-suggest-btn ai ${aiEnabled ? '' : 'locked'}`} onClick={() => void suggestAi()} disabled={aiBusy} title={aiEnabled ? 'Claude-powered tags & keywords' : 'Available on paid plans'}>
                <FiZap size={12} /> {aiBusy ? 'Thinking…' : aiEnabled ? 'AI suggest' : 'AI · Pro'}
              </button>
            </div>
            {aiMsg && <div className="kv-ai-msg">{aiMsg}</div>}
          </div>
        </div>

        <div className="kv-meta-row">
          <label><FiBell size={12} /> Review</label>
          <div className="kv-meta-field kv-review">
            <input type="date" value={reviewAt} onChange={e => { setReviewAt(e.target.value); markDirty(); }} />
            <select value={reviewEvery} onChange={e => { setReviewEvery(Number(e.target.value)); markDirty(); }}>
              {REPEAT_OPTS.map(o => <option key={o.days} value={o.days}>{o.label}</option>)}
            </select>
            <span className="kv-review-hint">remind me to revisit this</span>
            {reviewAt && reviewAt <= todayStr && <button className="kv-review-done" onClick={markReviewed} title="Mark reviewed — schedules the next review"><FiCheck size={12} /> Mark reviewed</button>}
            {reviewAt && <button className="kv-review-clear" onClick={() => { setReviewAt(''); setReviewEvery(0); markDirty(); }}>clear</button>}
          </div>
        </div>
      </div>

      <NoteEditor noteId={note.note_id} initialContent={draft.current.json} onChange={onEditorChange} />
    </div>
  );
};

export default NoteEntryEditor;
